import React, { useState, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useParams } from "react-router-dom";
import {
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  CircularProgress,
} from "@material-ui/core";
import LinearProgress from "@material-ui/core/LinearProgress";
import api from "../../../axios";
import Api from "../../../Api";
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import ImageSearchIcon from '@material-ui/icons/ImageSearch'; 

// --- STYLES ---
const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
  },
  card: {
    marginBottom: theme.spacing(3),
  },
  inputField: {
    height: "56px",
    display: "flex",
    alignItems: "center",
  },
  imageListContainer: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    paddingTop: theme.spacing(2),
  },
  imageBox: {
    width: 180, 
    padding: theme.spacing(1),
    margin: theme.spacing(0.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    textAlign: 'center',
  },
  imagePreview: {
    width: "100%",
    height: 120,
    objectFit: 'cover',
    borderRadius: theme.shape.borderRadius,
  },
  placeholder: {
    width: "100%",
    height: 120,
    backgroundColor: theme.palette.grey[200],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.shape.borderRadius,
  },
  statusText: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(0.5),
  },
  successText: {
    color: theme.palette.success.main,
    fontWeight: 'bold',
  },
  errorText: {
    color: theme.palette.error.main,
    fontWeight: 'bold',
  },
  uploadStatusBox: {
    display: 'flex',
    gap: theme.spacing(4),
    marginTop: theme.spacing(2),
  }
}));

const STATUS_IDLE = 'IDLE';
const STATUS_UPLOADING = 'UPLOADING';

// --- HELPER COMPONENT FOR PROGRESS BAR ---
function LinearProgressWithLabel(props) {
  return (
    <Box display="flex" alignItems="center" className={props.className}>
      <Box width="100%" mr={1}>
        <LinearProgress variant="determinate" {...props} />
      </Box>
      <Box minWidth={35}>
        <Typography variant="body2" color="textSecondary">{`${Math.round(
          props.value
        )}%`}</Typography>
      </Box>
    </Box>
  );
}

// --- MAIN COMPONENT ---
export default function View() {
  const classes = useStyles();
  const { auctionId, setId } = useParams();

  const [fileItems, setFileItems] = useState([]);
  const [uploadStatus, setUploadStatus] = useState(STATUS_IDLE);
  const [successCount, setSuccessCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0); 
  const [isReuploading, setIsReuploading] = useState(false);

  // PROGRESS CALCULATION
  const progress = useMemo(() => {
    if (fileItems.length === 0 || uploadStatus !== STATUS_UPLOADING) return 0;
    
    let totalItems = fileItems.length;
    
    // Kung nag re-reupload, ang base ng 100% ay ang bilang ng files na nire-reupload (status 'Ready' o 'Processing...')
    if (isReuploading) {
        // Sa re-upload, ang 'totalItems' ay ang bilang ng files na may status na 'Ready' o 'Processing...'
        totalItems = fileItems.filter(item => item.status === 'Ready' || item.status === 'Processing...').length;
    }
    
    if (totalItems === 0) return 100;

    return Math.min(100, (currentIndex / totalItems) * 100);
  }, [currentIndex, fileItems, uploadStatus, isReuploading]);

  // FILE INPUT HANDLER
  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const newFileItems = files.map(file => ({
      file,
      name: file.name,
      status: 'Ready', 
      message: '', 
      uploading: false,
    }));

    setFileItems(newFileItems);
    setSuccessCount(0);
    setErrorCount(0);
    setCurrentIndex(0);
    setUploadStatus(STATUS_IDLE);
    setIsReuploading(false); 
  };
  
  // COMMON API PROCESS FUNCTION
  const processImage = async (item, index, isReupload = false) => {
    const file = item.file;
    let newStatus, newMessage;
    
    // Para sa re-upload, kailangan nating i-reset ang error count sa item na ito kung ito ay galing sa error.
    if (isReupload) {
         // Temporarily decrement errorCount to correctly re-count errors/success in this batch
         if (item.status === 'Error') {
             setErrorCount(prev => prev - 1);
         }
    }

    // 1. Check size first (2MB limit)
    if (file.size > 2000000) {
        newStatus = 'Error';
        newMessage = 'Image To Large (Max 2MB)';
        
        // **FIX: I-increment ang errorCount DITO para sa size error**
        setErrorCount(prev => prev + 1); 
        
    } else {
        // Update individual status to show it's being processed
        setFileItems(prevItems => {
            const updatedItems = [...prevItems];
            const originalIndex = updatedItems.findIndex(i => i.name === item.name);
            if (originalIndex !== -1) {
                updatedItems[originalIndex].uploading = true;
                updatedItems[originalIndex].status = 'Processing...';
            }
            return updatedItems;
        });

        // 2. Prepare FormData
        let formData = new FormData();
        formData.append("auction_id", auctionId);
        formData.append("set_id", setId);
        formData.append("filename", file.name);
        formData.append("files", file);

        let config = {
            method: "post",
            maxBodyLength: Infinity,
            url: `${Api.request.URL}/lot/image/upload`,
            data: formData,
        };
        
        // 3. API Call
        try {
            const res = await api.request(config);
            newStatus = 'Success';
            newMessage = res.data.msg || (isReupload ? 'Re-upload Successful' : 'Upload Successful');
            setSuccessCount(prev => prev + 1);
        } catch (error) {
            newStatus = 'Error';
            newMessage = error.response?.data?.msg || (isReupload ? 'Re-upload Failed' : 'Upload Failed');
            
            // **FIX: I-increment ang errorCount DITO para sa API error**
            setErrorCount(prev => prev + 1); 
        }
    }
    
    // 4. Update the item's final status in the main fileItems array
    setFileItems(prevItems => {
        const updatedItems = [...prevItems];
        const originalIndex = updatedItems.findIndex(i => i.name === item.name);
        if (originalIndex !== -1) {
             updatedItems[originalIndex] = { 
                ...updatedItems[originalIndex], 
                status: newStatus, 
                message: newMessage,
                uploading: false 
            };
        }
        return updatedItems;
    });

    // 5. Update overall progress for this batch
    setCurrentIndex(prev => prev + 1);
  };
  
  // INITIAL UPLOAD HANDLER
  const handleUploadClick = async (e) => {
    e.preventDefault();
    if (fileItems.length === 0 || uploadStatus === STATUS_UPLOADING) return;

    setUploadStatus(STATUS_UPLOADING);
    setIsReuploading(false); 
    setSuccessCount(0);
    setErrorCount(0); // Reset for new full upload
    setCurrentIndex(0);
    
    for (let i = 0; i < fileItems.length; i++) {
        await processImage(fileItems[i], i, false);
    }
    
    setUploadStatus(STATUS_IDLE); 
  };
  
  // RE-UPLOAD ERRORS HANDLER
  const handleReuploadErrors = async (e) => {
    e.preventDefault();
    
    const errorsToReupload = fileItems.filter(item => item.status === 'Error');
    
    if (errorsToReupload.length === 0 || uploadStatus === STATUS_UPLOADING) return;
    
    // 1. I-reset ang status ng mga error files sa 'Ready' para sila ang ma-track at ma-recount
    setFileItems(prevItems => {
        return prevItems.map(item => {
            if (item.status === 'Error') {
                return { ...item, status: 'Ready', message: '', uploading: false };
            }
            return item;
        });
    });

    setUploadStatus(STATUS_UPLOADING);
    setIsReuploading(true); 
    setErrorCount(0); 
    setCurrentIndex(0); 

    // 2. Process errors sequentially
    for (let i = 0; i < errorsToReupload.length; i++) {
        await processImage(errorsToReupload[i], i, true);
    }
    
    // 3. Final state updates
    setUploadStatus(STATUS_IDLE); 
    setIsReuploading(false);
  };


  return (
    <div className={classes.root}>
      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Image File Uploader ({auctionId} / {setId})
          </Typography>
          <Grid container spacing={3} alignItems="center">
            {/* FILE INPUT */}
            <Grid item md={6} xs={12} className={classes.inputField}>
              <TextField
                type="file"
                variant="outlined"
                inputProps={{ accept: "image/jpeg", multiple: true }}
                multiple
                onChange={handleFileChange}
                fullWidth
              />
            </Grid>
            {/* SEND SERVER BUTTON (Initial Upload) */}
            <Grid item md={3} xs={12}>
              <Button
                fullWidth
                onClick={handleUploadClick}
                color="primary"
                variant="contained"
                style={{ height: "56px" }}
                disabled={fileItems.length === 0 || uploadStatus === STATUS_UPLOADING}
              >
                {uploadStatus === STATUS_UPLOADING && !isReuploading ? "Uploading All..." : "Send to Server"}
              </Button>
            </Grid>
             {/* RE-UPLOAD ERRORS BUTTON (New Feature) */}
            {errorCount > 0 && uploadStatus === STATUS_IDLE && (
                <Grid item md={3} xs={12}>
                    <Button
                        fullWidth
                        onClick={handleReuploadErrors}
                        color="secondary"
                        variant="outlined"
                        style={{ height: "56px" }}
                        disabled={uploadStatus === STATUS_UPLOADING}
                    >
                        {/* FIX: Gamitin ang errorCount state variable */}
                        {isReuploading ? `Re-uploading ${errorCount} Errors...` : `Re-upload ${errorCount} Error/s`}
                    </Button>
                </Grid>
            )}
          </Grid>
          
          {/* PROGRESS BAR AND COUNTS */}
          {fileItems.length > 0 && (
            <Box mt={2}>
              {/* COUNTS */}
              <Box className={classes.uploadStatusBox}>
                <Typography variant="subtitle1" style={{ color: 'black' }}>
                    Total: {fileItems.length}
                </Typography>
                <Typography variant="subtitle1" className={classes.successText}>
                    <CheckCircleOutlineIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                    Success: {successCount}
                </Typography>
                <Typography variant="subtitle1" className={classes.errorText}>
                    <ErrorOutlineIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                    Error: {errorCount}
                </Typography>
              </Box>

              {/* PROGRESS BAR */}
              {(uploadStatus === STATUS_UPLOADING || progress === 100) && (
                <Box mt={2}>
                  <LinearProgressWithLabel
                    style={{ height: "10px" }}
                    value={progress}
                  />
                  {progress === 100 && uploadStatus === STATUS_IDLE && (
                     <Typography variant="body2" color="textSecondary" align="center" style={{ marginTop: '4px' }}>
                        ✅ Upload Complete!
                     </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

        </CardContent>
      </Card>
      
      {/* IMAGE PREVIEWS AND STATUS */}
      {fileItems.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Image Status ({fileItems.length} Files)
          </Typography>
          <div className={classes.imageListContainer}>
            {fileItems.map((item, i) => (
              <div key={i} className={classes.imageBox}>
                {/* Image Preview */}
                {item.file ? (
                    <img
                        alt={item.name}
                        src={URL.createObjectURL(item.file)}
                        className={classes.imagePreview}
                    />
                ) : (
                    <Box className={classes.placeholder}>
                        <ImageSearchIcon color="action" />
                    </Box>
                )}
                
                {/* File Name */}
                <Typography variant="caption" noWrap title={item.name}>
                    {item.name}
                </Typography>

                {/* Status Indicator */}
                <Box className={classes.statusText}>
                    {item.uploading ? (
                        <CircularProgress size={16} color="primary" />
                    ) : item.status === 'Success' ? (
                        <>
                            <CheckCircleOutlineIcon fontSize="small" className={classes.successText} />
                            <Typography variant="caption" className={classes.successText} style={{ marginLeft: 4 }}>
                                Success
                            </Typography>
                        </>
                    ) : item.status === 'Error' ? (
                        <>
                            <ErrorOutlineIcon fontSize="small" className={classes.errorText} />
                            <Typography variant="caption" className={classes.errorText} style={{ marginLeft: 4 }}>
                                Error
                            </Typography>
                        </>
                    ) : (
                        <Typography variant="caption" color="textSecondary">
                            {item.status}
                        </Typography>
                    )}
                </Box>
                {/* Error Message */}
                {item.message && item.status !== 'Success' && (
                    <Typography variant="caption" className={classes.errorText} style={{ display: 'block' }}>
                        {item.message}
                    </Typography>
                )}
              </div>
            ))}
          </div>
        </Box>
      )}
    </div>
  );
}